import React, { useState } from 'react';
import { Search, FileText, MapPin, AlertCircle, Upload, X, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { DollarSign } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';

const API = process.env.REACT_APP_BACKEND_URL;

const SurveySearch = () => {
  const { toast } = useToast();
  const [surveyPlanFile, setSurveyPlanFile] = useState(null);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyAddress: '',
    requestType: '',
    northing: '',
    easting: '',
    additionalInfo: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please upload a file smaller than 10MB.",
          variant: "destructive"
        });
        return;
      }
      setSurveyPlanFile(file);
    }
  };

  const removeFile = () => {
    setSurveyPlanFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phone || !formData.propertyAddress || !formData.requestType) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // If no survey plan uploaded, require coordinates
    if (!surveyPlanFile && (!formData.northing || !formData.easting)) {
      toast({
        title: "Missing Information",
        description: "Please upload a survey plan or provide coordinates (Northing and Easting).",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/enquiries/survey-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          phone: formData.phone,
          email: formData.email || null,
          property_address: formData.propertyAddress,
          request_type: formData.requestType,
          northing: formData.northing || null,
          easting: formData.easting || null,
          additional_info: formData.additionalInfo || null,
          survey_plan_name: surveyPlanFile ? surveyPlanFile.name : null
        })
      });
      if (!res.ok) throw new Error('Submission failed');
      // Show payment prompt
      setShowPaymentPrompt(true);
    } catch (err) {
      toast({
        title: "Submission Failed",
        description: "We couldn't submit your request. Please try again or reach us on WhatsApp.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentConfirmation = () => {
    toast({
      title: "Request Submitted Successfully",
      description: "Our team will contact you within 24 hours with payment details and next steps.",
    });

    // Reset form
    setSurveyPlanFile(null);
    setShowPaymentPrompt(false);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      propertyAddress: '',
      requestType: '',
      northing: '',
      easting: '',
      additionalInfo: ''
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Survey Search & Land Verification | Versatile Geopick"
        description="Verify land status before you buy. Submit a survey search request with your survey plan or coordinates and protect your property investment."
      />
      {/* Payment Prompt Modal */}
      {showPaymentPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-10 animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <DollarSign className="h-10 w-10 text-emerald-700" />
              </div>
              <h2 className="text-3xl font-light text-stone-800 mb-4">
                Payment Required
              </h2>
              <p className="text-lg text-stone-600">
                To initiate your survey search, payment is required
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-amber-50/30 rounded-2xl p-8 mb-8 border border-emerald-200">
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-lg text-stone-700 mb-2">
                    Survey search fees vary based on property location and complexity
                  </p>
                  <p className="text-2xl font-semibold text-emerald-700">
                    Contact us for accurate pricing
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-8 text-sm text-stone-600">
              <p className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <span>Our team will contact you within 24 hours with exact fee quote</span>
              </p>
              <p className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <span>Pricing based on your property location and specific requirements</span>
              </p>
              <p className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <span>Survey search begins immediately after payment confirmation</span>
              </p>
              <p className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <span>Results delivered within 3-5 working days</span>
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
              <p className="text-sm text-stone-700">
                <strong className="text-amber-700">Payment Methods:</strong>
                <br />
                • Bank Transfer (Account details will be provided)
                <br />
                • Online Payment (Payment link will be sent)
                <br />
                • Cash Payment (At our office)
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1 border-2 border-stone-300 text-stone-700 hover:bg-stone-50 py-6 text-lg"
                onClick={() => setShowPaymentPrompt(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white py-6 text-lg"
                onClick={handlePaymentConfirmation}
              >
                Proceed with Request
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <Reveal className="text-center px-6" duration={0.9}>
          <div className="w-16 h-16 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Survey Search &<br /><span className="font-semibold text-emerald-700">Verification</span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mt-6">
            Verify land status and protect your property investment
          </p>
        </Reveal>
      </section>

      {/* Info Cards */}
      <section className="py-16 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-5xl mx-auto px-6">
          <RevealGroup className="grid md:grid-cols-3 gap-6">
            <RevealItem>
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-6 w-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-2">Document Verification</h3>
                <p className="text-stone-600 text-sm">
                  Authenticate survey plans at Surveyor General's office
                </p>
              </CardContent>
            </Card>
            </RevealItem>

            <RevealItem>
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="h-6 w-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-2">Land Status Check</h3>
                <p className="text-stone-600 text-sm">
                  Verify government acquisition & encumbrances
                </p>
              </CardContent>
            </Card>
            </RevealItem>

            <RevealItem>
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-6 w-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-2">Title Search</h3>
                <p className="text-stone-600 text-sm">
                  Comprehensive land title & ownership history
                </p>
              </CardContent>
            </Card>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal direction="scale">
          <Card className="border-2 border-stone-200 shadow-xl">
            <CardContent className="p-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-light text-stone-800 mb-3">Submit Request</h2>
                <p className="text-stone-600">
                  Fill in the details below and we'll process your request within 24 hours
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+234 XXX XXX XXXX"
                      className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Property Address/Location <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    placeholder="Enter full property address including street, area, LGA, and state"
                    rows={3}
                    className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Service Required <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="requestType"
                    value={formData.requestType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                  >
                    <option value="">Select a service</option>
                    <option value="survey-search">Survey Document Search & Verification</option>
                    <option value="land-status">Land Status & Government Acquisition Check</option>
                    <option value="title-search">Title Search & Validation</option>
                    <option value="new-survey">New Land Survey</option>
                    <option value="boundary-identification">Boundary Identification</option>
                    <option value="survey-plan">Survey Plan Preparation</option>
                  </select>
                </div>

                {/* Survey Plan Upload */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Upload Survey Plan (Optional)
                  </label>
                  <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 hover:border-emerald-500 transition-all duration-300">
                    {!surveyPlanFile ? (
                      <div className="text-center">
                        <Upload className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                        <p className="text-stone-600 mb-2">Click to upload or drag and drop</p>
                        <p className="text-sm text-stone-500 mb-4">PDF, JPG, PNG up to 10MB</p>
                        <input
                          type="file"
                          id="survey-plan-upload"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="survey-plan-upload"
                          className="inline-block px-6 py-3 bg-emerald-50 text-emerald-700 rounded-xl cursor-pointer hover:bg-emerald-100 transition-colors duration-300"
                        >
                          Choose File
                        </label>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-emerald-50 p-4 rounded-xl">
                        <div className="flex items-center gap-3">
                          <FileText className="h-6 w-6 text-emerald-700" />
                          <div>
                            <p className="font-medium text-stone-800">{surveyPlanFile.name}</p>
                            <p className="text-sm text-stone-600">
                              {(surveyPlanFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-2 hover:bg-emerald-100 rounded-lg transition-colors duration-300"
                        >
                          <X className="h-5 w-5 text-stone-600" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Coordinates Section */}
                <div className="border-2 border-stone-200 rounded-xl p-6 bg-stone-50">
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-stone-800 mb-1">
                        Property Coordinates {!surveyPlanFile && <span className="text-red-500">*</span>}
                      </h3>
                      <p className="text-sm text-stone-600">
                        {surveyPlanFile 
                          ? "Optional if survey plan is uploaded" 
                          : "Required if survey plan is not available"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Northing Coordinate {!surveyPlanFile && <span className="text-red-500">*</span>}
                      </label>
                      <Input
                        type="text"
                        name="northing"
                        value={formData.northing}
                        onChange={handleChange}
                        placeholder="e.g., 234567.890"
                        className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                      <p className="text-xs text-stone-500 mt-1">Enter the Northing value in meters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Easting Coordinate {!surveyPlanFile && <span className="text-red-500">*</span>}
                      </label>
                      <Input
                        type="text"
                        name="easting"
                        value={formData.easting}
                        onChange={handleChange}
                        placeholder="e.g., 456789.012"
                        className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                      <p className="text-xs text-stone-500 mt-1">Enter the Easting value in meters</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Additional Information
                  </label>
                  <Textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    placeholder="Please provide any additional details..."
                    rows={4}
                    className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex gap-4">
                  <AlertCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-stone-700">
                    <p className="font-medium mb-2">Important Information:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Our team will review your request and contact you within 24 hours</li>
                      <li>Upload your survey plan for faster verification</li>
                      <li>If survey plan unavailable, provide accurate coordinates</li>
                      <li>All information is kept confidential and secure</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-stone-300 text-stone-700 hover:bg-stone-50 px-8"
                    onClick={() => {
                      setSurveyPlanFile(null);
                      setFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        propertyAddress: '',
                        requestType: '',
                        northing: '',
                        easting: '',
                        additionalInfo: ''
                      });
                    }}
                  >
                    Clear Form
                  </Button>
                  <Button
                    type="submit"
                    data-testid="survey-search-submit"
                    disabled={isSubmitting}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white px-10 transition-all duration-300 disabled:opacity-60"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          </Reveal>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <Reveal className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-light text-stone-800 mb-4">
            Prefer to speak with us directly?
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-lg">
            <a href="tel:+2348034494507" className="text-emerald-700 hover:text-emerald-800 font-medium transition-colors duration-300">
              +234 803 449 4507
            </a>
            <span className="text-stone-300">|</span>
            <a href="https://wa.me/2348034494507" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-800 font-medium transition-colors duration-300">
              WhatsApp Us
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default SurveySearch;
